<!--
 * @Description: 首页商机金额柱状图
 * @Author: mdb
 * @Date: 2023-03-15 15:58:14
 * @LastEditTime: 2023-03-16 11:03:22
 * @LastEditors: mdb
-->
<template>
  <div class="home-echarts-wrap" :style="{ width, height }">
    <div class="home-echarts-header">
      <h3 class="home-echarts-header__title">{{ title }}</h3>
      <div class="home-echarts-header__controls">
        <CosRangePickerMonth
          v-model:startTime="queryForm.startTime"
          v-model:endTime="queryForm.endTime"
          :allowClear="props.allowClear"
          @change="getData"
        />
        <CosSelect
          v-model:value="queryForm.depCode"
          :allowClear="props.allowClear"
          style="width: 145px; margin-left: 10px"
          @change="getData"
        >
          <a-select-option value="all">全部数据</a-select-option>
          <a-select-option value="my">我的数据</a-select-option>
        </CosSelect>
      </div>
    </div>
    <!-- 延迟显示 loading 效果。当 spinning 状态在 delay 时间内结束，则不显示 loading 状态。 -->
    <a-spin :spinning="spinning" :delay="200">
      <!-- echarts 图表 -->
      <div ref="chartRef" class="home-echarts-content" v-show="showECharts"></div>
      <dc-layout-empty type="nothing" v-if="!showECharts" />
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onBeforeMount } from 'vue'
import { useECharts } from '@/hooks/useECharts'
import { getCurrentMonthDateRange } from '@/utils/timeFormat'
import CosRangePickerMonth from '@/components/CosRangePickerMonth/index.vue'
import { chanceSaleList as chanceSaleListFbu } from '@/api/gucangSales/index'
import { chanceSaleList as chanceSaleListLbu } from '@/api/yuntuSales/index'
import type { Ref, PropType } from 'vue'
interface QueryFormType {
  depCode: string
  startTime: string
  endTime: string
}
interface ItemCommonType {
  countTime: string
  salesAmount: number
}

const props = defineProps({
  title: {
    type: String as PropType<string>,
    default: '商机金额'
  },
  appId: {
    type: String as PropType<'cos_fbu' | 'cos_lbu'>,
    required: true
  },
  width: {
    type: String as PropType<string>,
    default: '100%'
  },
  height: {
    type: String as PropType<string>,
    default: '437px'
  },
  allowClear: {
    type: Boolean as PropType<boolean>,
    default: false
  }
})

// 函数枚举
const functionEnum = {
  cos_fbu: chanceSaleListFbu,
  cos_lbu: chanceSaleListLbu
}
const spinning = ref(false)
const showECharts = ref(false)
const { firstDayOfMonth, lastDayOfMonth } = getCurrentMonthDateRange()
const queryForm = reactive<QueryFormType>({
  depCode: 'all',
  startTime: firstDayOfMonth,
  endTime: lastDayOfMonth
})
const getData = () => {
  spinning.value = true
  functionEnum[props.appId](queryForm)
    .then((res) => {
      const data = res.data as unknown as ItemCommonType[]
      if (data.length === 0) {
        showECharts.value = false
        initChart([])
      } else {
        showECharts.value = true
        initChart(data)
      }
    })
    .finally(() => {
      spinning.value = false
    })
}

const chartRef = ref<HTMLDivElement | null>(null)
const { setOptions } = useECharts(chartRef as Ref<HTMLDivElement>)
const initChart = (data: ItemCommonType[]) => {
  setOptions({
    tooltip: {
      trigger: 'axis'
    },
    legend: {
      data: ['商机金额(万元)'],
      right: '50px'
    },
    toolbox: {
      feature: {
        magicType: {
          show: false,
          type: ['line', 'bar']
        }
      }
    },
    grid: {
      left: '80',
      right: '50',
      bottom: '50'
    },
    calculable: false,
    xAxis: [
      {
        type: 'category',
        data: data.map((item) => item.countTime)
        // axisLabel: {
        //   // 倾斜的程度
        //   formatter: function (value: any) {
        //     if (value.length > 7) {
        //       return `${value.slice(0, 7)}...`
        //     }
        //     return value
        //   }
        // }
      }
    ],
    yAxis: [
      {
        type: 'value',
        axisLine: {
          show: false,
          lineStyle: {
            type: 'dashed'
          }
        },
        axisLabel: {
          // 倾斜的程度
          margin: 2,
          formatter: function (value: any) {
            if (value >= 10000 && value < 100000000) {
              value = value / 10000 + '万'
            } else if (value >= 100000000 && value < 1000000000000) {
              value = value / 100000000 + '亿'
            } else if (value >= 1000000000000) {
              value = value / 1000000000000 + '兆'
            }
            return value
          }
        },
        splitLine: {
          lineStyle: {
            type: 'dashed'
          }
        }
      }
    ],
    series: [
      {
        name: '商机金额(万元)',
        type: 'bar',
        itemStyle: {
          color: '#3F97FF'
        },
        data: data.map((item) => item.salesAmount)
      }
    ]
  })
}
onBeforeMount(() => getData())
</script>
<script lang="ts">
export default { name: 'HomeBusinessAmountBarChart' }
</script>

<style lang="scss" scoped></style>
